import React from 'react';
import {CheckCircleIcon, ClockIcon} from '@heroicons/react/24/outline';
import {AUTOMATION_EVENT_LABELS, AUTOMATION_REASON_LABELS} from './automationEligibility';

export const formatAutomationAvailableAt = value => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    }).format(date);
};

export default function AutomationItemSelector({items, selectedIds, onChange, disabled = false}) {
    const available = items.filter(item => item.eligible);
    const blocked = items.filter(item => !item.eligible);
    const allSelected = available.length > 0 && available.every(item => selectedIds.includes(item.itemId));
    const toggle = id => onChange(selectedIds.includes(id)
        ? selectedIds.filter(value => value !== id) : [...selectedIds, id]);

    return (
        <div className="space-y-3">
            {available.length > 0 && <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Choose items <span className="font-normal text-gray-500 dark:text-gray-400">· {selectedIds.length}/{available.length} selected</span>
                </p>
                {available.length > 0 && (
                    <button type="button" disabled={disabled} className="mv-secondary-button !min-h-8 !px-3 !py-1.5 !text-xs"
                        onClick={() => onChange(allSelected ? [] : available.map(item => item.itemId))}>
                        <CheckCircleIcon className="h-4 w-4" />
                        {allSelected ? 'Clear items' : 'Select all items'}
                    </button>
                )}
            </div>}
            {available.length > 0 && selectedIds.length === 0 && (
                <p role="status" className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-400/10 dark:text-amber-200">
                    Select at least one item for this profile.
                </p>
            )}
            <div className="space-y-2">
                {available.map(item => {
                    const selected = selectedIds.includes(item.itemId);
                    return (
                        <label key={item.itemId} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${selected
                            ? 'border-cyan-300 bg-cyan-50 dark:border-panda-cyan/40 dark:bg-panda-cyan/10'
                            : 'border-gray-200 bg-white hover:border-cyan-300 dark:border-dark-700 dark:bg-dark-900 dark:hover:border-panda-cyan/40'}`}>
                            <input type="checkbox" checked={selected} disabled={disabled}
                                onChange={() => toggle(item.itemId)} aria-label={`Automate ${item.title}`}
                                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-cyan-700 dark:accent-panda-cyan" />
                            <span className="min-w-0">
                                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{item.title}</span>
                                {item.originalTitle && item.originalTitle !== item.title && (
                                    <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">Original item: {item.originalTitle}</span>
                                )}
                                <span className="mt-1 block text-xs text-cyan-800 dark:text-panda-cyan">
                                    {AUTOMATION_EVENT_LABELS[item.targetEventType] || item.targetEventType}
                                </span>
                            </span>
                        </label>
                    );
                })}
            </div>
            {blocked.length > 0 && (
                <details className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-dark-700 dark:bg-dark-850">
                    <summary className="cursor-pointer text-sm font-medium text-gray-600 dark:text-gray-300">
                        Unavailable items ({blocked.length})
                    </summary>
                    <div className="mt-3 space-y-3">
                        {blocked.map(item => (
                            <div key={item.itemId} className="flex items-start gap-2 text-sm">
                                <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                                <div className="min-w-0">
                                    <p className="font-medium text-gray-700 dark:text-gray-300">{item.title}</p>
                                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                        {AUTOMATION_REASON_LABELS[item.reason] || item.reason}
                                        {item.availableAt && ` · Available ${formatAutomationAvailableAt(item.availableAt)}`}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}
